import { useState, useEffect } from 'react';
import { Scale } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { PAGE_META } from '@/data/pageMeta';
import usePageMeta from '@/hooks/usePageMeta';
import shared from '@/styles/public.module.css';
import formStyles from '@/styles/form.module.css';
import shell from '@/styles/authShell.module.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InviteAccept = () => {
  usePageMeta(PAGE_META.inviteAccept);

  const { token } = useParams();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/invites/${token}`, { credentials: 'include' });
        if (!r.ok) {
          const data = await r.json().catch(() => ({}));
          setError(data.detail || 'This invitation is no longer valid.');
        } else {
          setInvite(await r.json());
        }
      } catch (e) {
        setError('Could not load the invitation. Please try again later.');
      } finally { setLoading(false); }
    })();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password || password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (password !== confirm) { toast.error('Passwords do not match'); return; }
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/invites/${token}/accept`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Could not activate account');
      localStorage.setItem('access_token', data.access_token);
      toast.success('Account activated! Redirecting…');
      setTimeout(() => navigate('/lawyer', { state: { user: data.user }, replace: true }), 800);
    } catch (e) {
      toast.error(e.message);
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <div className={shell.page}>
        <main className={shell.centre}><p>Loading invitation…</p></main>
      </div>
    );
  }

  if (error) {
    return (
      <div className={shell.page}>
        <main className={shell.centre}>
          <div className={shell.inner}>
            <div className={shell.card} data-testid="invite-error">
              <div className={shell.head}>
                <div className={`${shell.statusIcon} ${shell.statusError}`}>
                  <Scale size={30} aria-hidden="true" />
                </div>
                <h1 className={shell.title}>Invitation Issue</h1>
                <p className={shell.subtitle}>{error}</p>
              </div>
              <p className={shell.footNote}>
                Please contact your attorney for a new invitation link.
              </p>
              <button type="button" className={`${shared.btn} ${shared.btnQuiet} ${shell.fullBtn} ${shell.spacedTop}`}
                onClick={() => navigate('/login')}>
                Go to Login
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className={shell.page}>
      <header className={shell.header}>
        <div className={shell.headerInner}>
          <button type="button" className={shell.logo} onClick={() => navigate('/')} aria-label="MyCaseLoan home">
            <Scale size={26} aria-hidden="true" />
            <span className={shell.logoText}>MyCaseLoan</span>
          </button>
        </div>
      </header>
      <main className={shell.centre}>
        <div className={shell.inner}>
          <div className={shell.card} data-testid="invite-accept-page">
            <div className={shell.head}>
              <h1 className={shell.title}>Create Your Password</h1>
              <p className={shell.subtitle}>
                Welcome <strong>{invite.first_name} {invite.last_name}</strong>! Your email{' '}
                <strong>{invite.email}</strong> has already been verified.
                {invite.lawFirmName ? <> You&apos;re joining <strong>{invite.lawFirmName}</strong>.</> : null}
                {' '}Set a password to activate your account.
              </p>
            </div>
            <form onSubmit={handleSubmit}>
              <div className={formStyles.field}>
                <label className={formStyles.label} htmlFor="invite-pw">Password</label>
                <input id="invite-pw" className={formStyles.control} type="password" value={password}
                  onChange={(e) => setPassword(e.target.value)} required minLength={6} data-testid="invite-password" />
                <p className={formStyles.hint}>Minimum 6 characters.</p>
              </div>
              <div className={formStyles.field}>
                <label className={formStyles.label} htmlFor="invite-pw2">Confirm Password</label>
                <input id="invite-pw2" className={formStyles.control} type="password" value={confirm}
                  onChange={(e) => setConfirm(e.target.value)} required minLength={6} data-testid="invite-password-confirm" />
              </div>
              <button type="submit" className={`${shared.btn} ${shared.btnAction} ${shell.fullBtn}`}
                disabled={submitting} data-testid="invite-submit">
                {submitting ? 'Activating…' : 'Create Password & Sign In'}
              </button>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
};

export default InviteAccept;
